import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, App, Typography } from 'antd';
import { DeleteOutlined, LeftOutlined } from '@ant-design/icons';
import { getPlanById, createPlan, updatePlan, checkPlanExists, listPlanPermissions } from '../../services/plans';
import { List } from 'antd';
import { Popconfirm } from 'antd';
import { InputNumber } from 'antd';
import { Switch } from 'antd';
import { Modal } from 'antd';
import { Select, ColorPicker } from 'antd';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;

const { useApp } = App;

const PlanUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const { canAny } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [benefits, setBenefits] = useState({});
  const [actions, setActions] = useState([]);

  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [permissionForm] = Form.useForm();


  useEffect(() => {
    listPlanPermissions()
        .then((actionsData) => {
            setActions(actionsData);

        }).catch(() => {
        message.error('No se pudo cargar la membresía');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [message]);


  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getPlanById(id)
      .then((plan) => {
        form.setFieldsValue({
          code: plan.code?.trim(),
          name: plan.name?.trim(),
          description: plan.description?.trim(),
          durationDays: Number(plan.durationDays),
          price: Number(plan.price),
          currency: plan.currency?.trim(),
          isTrial: plan.isTrial || false,
          isActive: plan.isActive || false,
          color: plan.color || '#BA955E',
        });
        setBenefits(plan.benefits || {});
      })
      .catch(() => {
        message.error('No se pudo cargar la membresía');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      const exists = await checkPlanExists(values.description.trim(),id);
      if(exists === true){
        message.warning('Ya existe una membresía con la descripción proporcionada');
        return;
      }

        const payload = {
            code: values.code?.trim(),
            name: values.name?.trim(),
            description: values.description?.trim(),
            durationDays: values.durationDays || 30,
            price: values.price || 0,
            currency: values.currency?.trim(),
            isTrial: values.isTrial || false,
            isActive: values.isActive,
            color: typeof values.color === 'string' ? values.color : (values.color?.toHexString?.() || values.color),
            benefits: benefits,
        };

      if (isEdit && id) {
        await updatePlan(id, payload);
        message.success('Membresía actualizada correctamente');
      } else {
        await createPlan(payload);
        message.success('Membresía agregada');
      }
      navigate('/memberships/all')
    } catch (err) {
      // Extraer datos del error
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al guardar el usuario';
      
      // Si es un error de validación (422), mostrar errores en los campos del formulario
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        console.log('Issues encontrados:', issues);
        
        if (issues.length > 0) {
          // Limpiar errores previos
          form.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message) 
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              console.log('Mapeando error para campo:', fieldPath, 'Mensaje:', issue.message);
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          console.log('Field errors a establecer:', fieldErrors);
          
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
            console.log('Errores establecidos en el formulario');
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/memberships/all');
  };

  return (
    <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button 
                type="text" 
                icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
                style={{ 
                padding: 0,
                color: '#BA955E',
                fontSize: '24px',
                fontWeight: '700',
                height: 'auto'
                }}
                onClick={handleBack}
            />
            <Title level={3} style={{ marginBottom: 0 }}>
            {isEdit ? 'Editar Membresía' : 'Agregar Membresía'}
            </Title>
        </div>
        <Card loading={loading}>
            <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            autoComplete="off"
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Código"
                            name="code"
                            rules={[
                            { required: true, message: 'El código es requerido' },
                            ]}
                        >
                            <Input placeholder="AFICIONADO" allowClear />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Membresía"
                            name="name"
                            rules={[
                            { required: true, message: 'El nombre de la membresía es requerido' },
                            ]}
                        >
                            <Input placeholder="Membresía" allowClear />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={18}>
                        <Form.Item
                            label="Descripción"
                            name="description"
                            rules={[
                            { required: true, message: 'La descripción es requerida' },
                            ]}
                        >
                            <Input placeholder="Membresía gratuito" allowClear />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item
                            label="Duración de la membresía (días)"
                            name="durationDays"
                            rules={[
                            { required: true, message: 'El nombre de la membresía es requerido' },
                            { type: 'number', min: 1, message: 'No puedes ingresar un valor menor a 1', },
                            ]}
                        >
                            <InputNumber placeholder="30 días" min={1} style={{width: '100%'}} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={5}>
                        <Form.Item
                            label="Precio"
                            name="price"
                            rules={[
                            { required: true, message: 'El precio de la membresía es requerido' },
                            { type: 'number', min: 0, message: 'No puedes ingresar un valor menor a 0', },
                            ]}
                        >
                            <InputNumber placeholder="199" min={0} style={{width: '100%'}} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={5}>
                        <Form.Item
                            label="Monenda"
                            name="currency"
                            rules={[
                            { required: true, message: 'La moneda de la membresía es requerido' },
                            ]}
                        >
                            <Input placeholder="MXN" allowClear />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Item
                            label="Color"
                            name="color"
                        >
                            <ColorPicker showText />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={5}>
                        <Form.Item
                            label="¿Incluir periodo de prueba?"
                            name="isTrial"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={5}>
                        <Form.Item
                            label="¿Activar membresía?"
                            name="isActive"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
                <Row justify="end" style={{ marginBottom: 8 }}>
                    <Button
                        type="dashed"
                        onClick={() => setPermissionModalOpen(true)}
                    >
                        + Agregar beneficio
                    </Button>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Title level={5}>Beneficios de la membresía</Title>
                        <List
                            bordered
                            dataSource={Object.entries(benefits)}
                            locale={{ emptyText: 'Sin beneficios configurados' }}
                            renderItem={([key, value]) => (
                            <List.Item>
                            <Row gutter={16} align="middle" style={{ width: '100%' }}>
                                
                                {/* Label */}
                                <Col span={6}>
                                <strong>{value.label || key}</strong>
                                </Col>

                                {/* Permitido */}
                                <Col span={6}>
                                <Switch
                                    checked={value.can}
                                    onChange={(checked) =>
                                    setBenefits((prev) => ({
                                        ...prev,
                                        [key]: { ...prev[key], can: checked },
                                    }))
                                    }
                                />
                                <span style={{ marginLeft: 8 }}>
                                    {value.can ? 'Permitido' : 'No permitido'}
                                </span>
                                </Col>

                                {/* Límite */}
                                <Col span={8}>
                                <InputNumber
                                    min={1}
                                    placeholder="Ilimitado"
                                    value={value.limit}
                                    disabled={!value.can}
                                    onChange={(limit) =>
                                    setBenefits((prev) => ({
                                        ...prev,
                                        [key]: {
                                        ...prev[key],
                                        limit: limit ?? undefined,
                                        },
                                    }))
                                    }
                                />
                                </Col>

                                {/* Eliminar */}
                                <Col span={4} style={{ textAlign: 'right' }}>
                                <Popconfirm
                                    title="¿Eliminar beneficio?"
                                    onConfirm={() => {
                                    setBenefits((prev) => {
                                        const copy = { ...prev };
                                        delete copy[key];
                                        return copy;
                                    });
                                    }}
                                >
                                    <DeleteOutlined style={{ color: '#ff4d4f' }} />
                                </Popconfirm>
                                </Col>

                            </Row>
                            </List.Item>
                        )}
                        />
                    </Col>
                </Row>
                <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
                    <Col>
                    <Button onClick={() => navigate(-1)} disabled={loading}>
                        Cancelar
                    </Button>
                    </Col>
                    {canAny(['memberships.create', 'memberships.update']) && (<Col>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {isEdit ? 'Guardar cambios' : 'Guardar'}
                        </Button>
                    </Col>)}
                </Row>
            </Form>
        </Card>
        <Modal
            title="Agregar beneficio"
            open={permissionModalOpen}
            onCancel={() => {
                permissionForm.resetFields();
                setPermissionModalOpen(false);
            }}
            onOk={async () => {
                try {
                const values = await permissionForm.validateFields();

                const key = values.key.trim();

                if (benefits[key]) {
                    message.error('Este permiso ya existe');
                    return;
                }

                setBenefits((prev) => ({
                    ...prev,
                    [key]: {
                    label: values.label,
                    can: values.can ?? true,
                    limit: values.limit ?? undefined,
                    },
                }));

                permissionForm.resetFields();
                setPermissionModalOpen(false);
                } catch {}
            }}
            >
            <Form form={permissionForm} layout="vertical">
                <Form.Item label="Beneficio" name="key">
                    <Select
                        showSearch
                        placeholder="Selecciona o escribe una clave"
                        options={actions.map(a => ({
                        value: a.key,
                        label: `${a.key} — ${a.label}`,
                        }))}
                        onChange={(value) => {
                            const action = actions.find(a => a.key === value);
                            if (action) {
                                permissionForm.setFieldsValue({
                                label: action.label,
                                });
                            }
                        }}
                    />
                </Form.Item>

                <Form.Item
                    label="Etiqueta"
                    name="label"
                    rules={[{ required: true, message: 'La etiqueta es obligatoria' }]}
                >
                    <Input placeholder="Subir videos" />
                </Form.Item>

                <Form.Item
                    label="¿Permitido?"
                    name="can"
                    valuePropName="checked"
                    initialValue={true}
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    label="Límite (opcional)"
                    name="limit"
                >
                    <InputNumber min={1} placeholder="Ilimitado" />
                </Form.Item>
            </Form>
        </Modal>

    </div>
  );
};

export default PlanUpsertPage;

